import { inject, Injectable, NgZone } from '@angular/core';
import { AMPLITUDE_CONFIG } from '../../providers/amplitude-config/amplitude-config.token';
import { LogService } from 'shared/core/data-access/log';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import {
    AbstractTrackingService,
    TrackingUserInfo,
} from 'shared/core/data-access/analytics-types';
import { ServerZoneEnum } from '../../enums/server-zone/server-zone.enum';
import { VERSION } from 'shared/core/data-access/version';

@Injectable({ providedIn: 'root' })
export class AmplitudeService extends AbstractTrackingService {
    config = inject(AMPLITUDE_CONFIG);
    zone = inject(NgZone);
    #logService = inject(LogService);
    #frontendVersion = inject(VERSION);

    override init(userInfo?: TrackingUserInfo) {
        this.zone.runOutsideAngular(() => {
            if (this.config.key) {
                this.#logService.debug('Initializing Amplitude');
                try {
                    amplitude.init(this.config.key, userInfo?.userId, {
                        serverZone: this.config.serverZone ?? ServerZoneEnum.EU,
                        autocapture: this.config.autocapture ?? false,
                        fetchRemoteConfig:
                            this.config.fetchRemoteConfig ?? false,
                    });

                    const identifyEvent = new Identify();
                    identifyEvent.set('user_roles', userInfo?.roles ?? []);
                    identifyEvent.set('host_name', window.location.hostname);
                    identifyEvent.set(
                        'frontend_version',
                        this.#frontendVersion,
                    );
                    amplitude.identify(identifyEvent);

                    this.#logService.debug(
                        'Amplitude initialized successfully.',
                    );
                } catch (error) {
                    this.#logService.error(
                        'Failed to initialize Amplitude:',
                        error,
                    );
                }
            }
        });
    }

    override trackEvent(eventName: string, eventData: Record<string, unknown>) {
        amplitude.track(eventName, eventData);
    }
}
